import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {
    BACK_OFFICE_URL_PATH,
    Balance,
    BalanceResponse,
    BILLING_URL_PATH, GenericResponse,
    MakePaymentResponse,
    Movement, MovementDto,
    PaymentMethod,
    PaymentMethodCreation,
    PaymentMethodCreationResponse,
    PaymentMethodGet,
    PaymentMethodGetResponse,
    RatePlan,
    RatePlanItem,
    RatePlanResponse,
    SERVER_URL
} from '../contexts/Configuration';

type InitialState = {
    waitingForBalance: boolean,
    balanceErrorMessage: string | null

    balanceCredit: number,
    availableMinutes: number,
    ratePlanItems: RatePlanItem[],
    movements: Movement[],

    waitingForRatePlan: boolean,
    ratePlan: RatePlan | null,

    waitingForCustomerMovements: boolean,
    haveValidCustomer: boolean,
    customerMovements: Movement[],

    waitingForAdjustment: boolean;

    waitingForPaymentMethod: boolean,
    paymentMethod: PaymentMethodGet | null,
    paymentMethodErrorMessage: string | null,

    waitingForPayment: boolean,
    paymentErrorMessage: string | null,
}

const initialState: InitialState = {
    waitingForBalance: true,
    balanceErrorMessage: null,

    balanceCredit: 0,
    availableMinutes: 0,

    waitingForRatePlan: true,
    ratePlan: null,

    ratePlanItems: [],
    movements: [],

    waitingForCustomerMovements: false,
    haveValidCustomer: false,
    customerMovements: [],

    waitingForAdjustment: false,

    waitingForPaymentMethod: true,
    paymentMethod: null,
    paymentMethodErrorMessage: null,

    waitingForPayment: false,
    paymentErrorMessage: null
}

const marshalResponse = (response: BalanceResponse) => {
    if (response.status === 200) return response.json();
    // if (response.status === 400) return response.json().then(r => throw new Error(r.message));
    throw new Error(`Fail to get Balance (error status ${response.status} - ${response.type}): ${response.statusText}`);
}

const marshalPaymentCreationResponse = (response: PaymentMethodCreationResponse) => {
    if (response.status === 200) return response.json();
    // if (response.status === 400) return response.json().then(r => throw new Error(r.message));
    throw new Error(`Fail to get Balance (error status ${response.status} - ${response.type}): ${response.statusText}`);
}

const marshalPaymentsResponse = (response: PaymentMethodGetResponse) => {
    if (response.status === 200) return response.json();
    // if (response.status === 400) return response.json().then(r => throw new Error(r.message));
    throw new Error(`Fail to get Balance (error status ${response.status} - ${response.type}): ${response.statusText}`);
}

const marshalMakePaymentResponse = (response: MakePaymentResponse) => {
    if (response.status === 200) return response.json();
    // if (response.status === 400) return response.json().then(r => throw new Error(r.message));
    throw new Error(`Fail to get Balance (error status ${response.status} - ${response.type}): ${response.statusText}`);
}

// === for backoffice ===
const marshalRatePlanResponse = (response: RatePlanResponse) => {
    if (response.status === 200) return response.json();
    // if (response.status === 400) return response.json().then(r => throw new Error(r.message));
    throw new Error(`Fail to get Balance (error status ${response.status} - ${response.type}): ${response.statusText}`);
}


// ============ ASYNC ACTIONS ============
//<ReturnType, ArgsType, ThunkAPI type>

// === BALANCE ===
export const getBalance = createAsyncThunk<
    Balance,
    { token: string, withMov?: boolean, timeZone?: string },
    { rejectValue: string }
>(
    'billing/balance',
    async (params, {rejectWithValue}) => {
        const dateTimeOptions = Intl.DateTimeFormat().resolvedOptions();

        const withMovs = params.withMov || false;
        const queryMovs = withMovs ? `?withMov=true&timeZone=${dateTimeOptions.timeZone}` : '';

        return await fetch(`${SERVER_URL}${BILLING_URL_PATH}/balance${queryMovs}`, {
            method: 'GET',
            headers: {Authorization: `Bearer ${params.token}`}
        })
            .then(response => marshalResponse(response as BalanceResponse))
            .then(response => {
                return response;
            })
            .catch(error => rejectWithValue(error.message))
    }
);


// === PAYMENT METHOD ===
export const getPaymentMethod = createAsyncThunk<
    PaymentMethodGet,
    { token: string },
    { rejectValue: string }
>(
    'billing/paymentMethod/get',
    async (params, {rejectWithValue}) => {

        return await fetch(`${SERVER_URL}${BILLING_URL_PATH}/payment-method`, {
            method: 'GET',
            headers: {Authorization: `Bearer ${params.token}`}
        })
            .then(response => marshalPaymentsResponse(response as PaymentMethodGetResponse))
            .then(response => {
                // console.log('Got Payment Methods ???? -> ', response);
                return response;
            })
            .catch(error => rejectWithValue(error.message));
    }
);

export const savePaymentMethod = createAsyncThunk<
    PaymentMethodCreation,
    { token: string },
    { rejectValue: string }
>(
    'billing/paymentMethod/create',
    async (params, {rejectWithValue}) => {

        return await fetch(`${SERVER_URL}${BILLING_URL_PATH}/payment-method`, {
            method: 'POST',
            headers: {Authorization: `Bearer ${params.token}`}
        })
            .then(response => marshalPaymentCreationResponse(response as PaymentMethodCreationResponse))
            .then(response => {
                console.log('Got secret???? -> ', response);
                return response;
            })
            .catch(error => rejectWithValue(error.message))
    }
);

export const makePayment = createAsyncThunk<
    GenericResponse,
    { token: string, amount: number, currency: string, paymentMethod: PaymentMethod },
    { rejectValue: string }
>(
    'billing/payment',
    async (params, {rejectWithValue}) => {

        const body = {
            stripeCustomer: params.paymentMethod.stripeCustomer,
            paymentMethodId: params.paymentMethod.paymentMethodId,
            amount: params.amount,
            currencyCode: params.currency

            // movementType: 'pre-paid', // TBD if this is dynamic
            // currency: payload.currency,
            // paymentMethodId: payload.methodId,
            // amount: payload.amount,
        }

        return await fetch(`${SERVER_URL}${BILLING_URL_PATH}/payment`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${params.token}`
            },
            body: JSON.stringify(body)
        })
            .then(response => marshalMakePaymentResponse(response as MakePaymentResponse))
            // .then(response => response)
            .catch(error => rejectWithValue(error.message))
    }
)

// === FOR BACKOFFICE ===
export const retrieveRatePlan = createAsyncThunk<
    RatePlan,
    { token: string },
    { rejectValue: string }
>(
    'billing/backoffice/retrieveRatePlan',
    async (params, {rejectWithValue}) => {

        return await fetch(`${SERVER_URL}${BACK_OFFICE_URL_PATH}/rateplan`, {
            method: 'GET',
            headers: {Authorization: `Bearer ${params.token}`}
        })
            .then(response => marshalRatePlanResponse(response as RatePlanResponse))
            // .then(response => ({items: response.ratePlanItems}))
            .catch(error => rejectWithValue(error.message))
    }
)

export const updateRatePlan = createAsyncThunk<
    RatePlan,
    { token: string, items: RatePlanItem[] },
    { rejectValue: string }
>(
    'billing/backoffice/updateRatePlan',
    async (params, {rejectWithValue}) => {

        console.log('updateRatePlan -> ', JSON.stringify([...params.items]))

        return await fetch(`${SERVER_URL}${BACK_OFFICE_URL_PATH}/rateplan`, {
            method: 'POST',
            headers: {Authorization: `Bearer ${params.token}`, 'content-type': 'application/json'},
            body: JSON.stringify([...params.items])
        })
            .then(response => marshalRatePlanResponse(response as RatePlanResponse))
            // .then(response => ({items: response.ratePlanItems}))
            .catch(error => rejectWithValue(error.message))
    }
)

export const retrieveCustomerMovements = createAsyncThunk<
    Balance,
    { token: string, customerEmail: string },
    { rejectValue: string }
>('billing/backoffice/retrieveCustomerMovements',
    async (params, {rejectWithValue}) => {
        const dateTimeOptions = Intl.DateTimeFormat().resolvedOptions();

        return await fetch(`${SERVER_URL}${BACK_OFFICE_URL_PATH}/balance?customerEmail=${params.customerEmail}&timeZone=${dateTimeOptions.timeZone}`, {
            method: 'GET',
            headers: {Authorization: `Bearer ${params.token}`}
        })
            .then(response => marshalResponse(response as BalanceResponse))
            .then(response => response)
            .catch(error => rejectWithValue(error.message))
    }
)

export const createAdjustMovement = createAsyncThunk<
    {},
    { token: string, movement: MovementDto },
    { rejectValue: string }
>('billing/backoffice/createAdjustMovement',
    async (params, {rejectWithValue}) => {
        return await fetch(`${SERVER_URL}${BACK_OFFICE_URL_PATH}/movements`, {
            method: 'POST',
            headers: {Authorization: `Bearer ${params.token}`, 'content-type': 'application/json'},
            // body: JSON.stringify( {...params.movement, total: params.movement.total?.toString(2)})
            body: JSON.stringify(params.movement)
        })
            .then(response => marshalResponse(response as BalanceResponse))
            .then(response => response)
            .catch(error => rejectWithValue(error.message))
    }
)


const billingSlice = createSlice({
    name: 'billing',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getBalance.pending, (state) => {
            state.waitingForBalance = true;
        })
        builder.addCase(getBalance.fulfilled, (state, {payload}) => {
            state.waitingForBalance = false;
            state.balanceCredit = payload.balanceCredit;
            state.availableMinutes = payload.availableMinutes;

            state.ratePlanItems = payload.ratePlanItems;
            state.movements = payload.movements || [];
        })
        builder.addCase(getBalance.rejected, (state, {payload}) => {
            state.waitingForBalance = false;
            state.balanceErrorMessage = payload || ''
            console.log(' --> getBalance.rejected ==>', payload)
        })


        // === PAYMENT METHODS ==
        builder.addCase(savePaymentMethod.pending, (state) => {
            // console.log('savePaymentMethod.pending!')
        })
        builder.addCase(savePaymentMethod.fulfilled, (state, {payload}) => {
            // console.log('savePaymentMethod.fulfilled ==> ', payload)
        })
        builder.addCase(savePaymentMethod.rejected, (state, {payload}) => {
            console.log('savePaymentMethod.rejected ==>', payload)
        })

        builder.addCase(getPaymentMethod.pending, (state) => {
            state.waitingForPaymentMethod = true;
            // console.log('getPaymentMethod.pending!')
        })
        builder.addCase(getPaymentMethod.fulfilled, (state, {payload}) => {
            state.paymentMethod = payload;
            state.waitingForPaymentMethod = false;
            // console.log('getPaymentMethod.fulfilled ==> ', payload)
        })
        builder.addCase(getPaymentMethod.rejected, (state, {payload}) => {
            state.paymentMethodErrorMessage = payload || 'Unknown error'
            state.waitingForPaymentMethod = false;
            console.log('getPaymentMethod.rejected ==>', payload)
        })

        // === PAYMENT ==
        builder.addCase(makePayment.pending, (state) => {
            state.waitingForPayment = true;
        })
        builder.addCase(makePayment.fulfilled, (state, {payload}) => {
            state.waitingForPayment = false;
        })
        builder.addCase(makePayment.rejected, (state, {payload}) => {
            state.paymentErrorMessage = payload || 'Unknown error'
            state.waitingForPayment = false;
            console.log('--> makePayment.rejected ==>', payload)
        })


        // === BACKOFFICE - RATE PLAN ===
        builder.addCase(retrieveRatePlan.pending, (state) => {
            state.waitingForRatePlan = true;
            state.ratePlan = null;
        })
        builder.addCase(retrieveRatePlan.fulfilled, (state, {payload}) => {
            state.waitingForRatePlan = false;
            state.ratePlan = payload;
        })
        builder.addCase(retrieveRatePlan.rejected, (state, {payload}) => {
            state.waitingForRatePlan = false;
            console.log('--> retrieveRatePlan.rejected ==>', payload)
        })

        builder.addCase(updateRatePlan.pending, (state) => {
            state.waitingForRatePlan = true;
            state.ratePlan = null;
        })
        builder.addCase(updateRatePlan.fulfilled, (state, {payload}) => {
            state.waitingForRatePlan = false;
            state.ratePlan = payload;
        })
        builder.addCase(updateRatePlan.rejected, (state, {payload}) => {
            state.waitingForRatePlan = false;
            console.log('--> updateRatePlan.rejected ==>', payload)
        })

        // === BACKOFFICE - CUSTOMER ===
        builder.addCase(retrieveCustomerMovements.pending, (state) => {
            state.waitingForCustomerMovements = true;
            state.haveValidCustomer = false;
            state.customerMovements = [];
        })
        builder.addCase(retrieveCustomerMovements.fulfilled, (state, {payload}) => {
            state.waitingForCustomerMovements = false;
            state.haveValidCustomer = true;
            state.customerMovements = payload.movements;
        })
        builder.addCase(retrieveCustomerMovements.rejected, (state, {payload}) => {
            state.waitingForCustomerMovements = false;
            state.haveValidCustomer = false;
            console.log('--> retrieveCustomerMovements.rejected ==>', payload)
        })

        builder.addCase(createAdjustMovement.pending, (state) => {
            state.waitingForAdjustment = true;
        })
        builder.addCase(createAdjustMovement.fulfilled, (state, {payload}) => {
            state.waitingForAdjustment = false;
        })
        builder.addCase(createAdjustMovement.rejected, (state, {payload}) => {
            state.waitingForAdjustment = false;
            console.log('--> createAdjustMovement.rejected ==>', payload)
        })
    }
});

export default billingSlice.reducer;
